import { api } from '@/services/api';
import { isInNativeWrapper, notifyNativeStartGpsTracking, notifyNativeStopGpsTracking } from '@/services/nativeBridge';

/**
 * GPS tracking continuo per selettiva accettata.
 *
 * Due implementazioni a runtime:
 *  - Browser/PWA: interval JS che ogni 10s legge navigator.geolocation e
 *    fa POST. Si ferma se l'utente chiude la tab (limite browser).
 *  - Wrapper nativo: delega al modulo backgroundGps via vvfNativeBridge.
 *    Il task gira come foreground service Android — continua anche se
 *    l'utente chiude l'app, finche' non riceve stop oppure la selettiva
 *    viene terminata server-side.
 *
 * Singleton: una sola selettiva trackata alla volta.
 */

const TRACK_INTERVAL_MS = 10_000;
const STORAGE_KEY = 'vvf.gpsTracking.selectiveId';

let intervalId: number | null = null;
let currentSelectiveId: string | null = null;
const listeners = new Set<(active: string | null) => void>();

function notify(): void {
  listeners.forEach(l => l(currentSelectiveId));
}

function getPosition(): Promise<{ lat: number; lng: number } | null> {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000 },
    );
  });
}

async function tick(selectiveId: string): Promise<void> {
  const pos = await getPosition();
  if (!pos) return;
  try {
    await api.post(`/selectives/${selectiveId}/respond`, {
      response: 'Accepted',
      latitude: pos.lat,
      longitude: pos.lng,
    });
  } catch (err: unknown) {
    // 404: selettiva terminata server-side (RespondToSelectiveHandler ritorna
    // NotFound se Status != Active). Continuare a chiamare ogni 10s spamma
    // i log e mantiene attivo l'interval inutilmente. Fermiamo il tracking.
    const e = err as { response?: { status?: number } };
    if (e?.response?.status === 404) {
      // Stop senza notifyNativeStop (siamo gia' fuori dal flow normale).
      // Lo stop completo (clearInterval + state) e' gestito da gpsTracking.stop().
      gpsTracking.stop();
    }
    // Altri errori: silenzio, riproveremo al prossimo tick (es. network blip).
  }
}

export const gpsTracking = {
  start(selectiveId: string): void {
    if (currentSelectiveId === selectiveId && (intervalId !== null || isInNativeWrapper())) return;
    this.stop();
    currentSelectiveId = selectiveId;
    localStorage.setItem(STORAGE_KEY, selectiveId);
    if (isInNativeWrapper() && notifyNativeStartGpsTracking(selectiveId)) {
      // Il task nativo gira da solo: niente interval JS.
      notify();
      return;
    }
    // Fallback browser: primo tick subito, poi ogni 10s
    void tick(selectiveId);
    intervalId = window.setInterval(() => void tick(selectiveId), TRACK_INTERVAL_MS);
    notify();
  },

  stop(): void {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (isInNativeWrapper()) {
      notifyNativeStopGpsTracking();
    }
    currentSelectiveId = null;
    localStorage.removeItem(STORAGE_KEY);
    notify();
  },

  getActive(): string | null {
    return currentSelectiveId;
  },

  /** Riattiva il tracking dopo refresh se c'era una selettiva accettata. */
  resumeFromStorage(): void {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && currentSelectiveId !== saved) {
      this.start(saved);
    }
  },

  onChange(cb: (active: string | null) => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
};
